/**
 * MonJournal — Journal de lecture personnel (route /parcours).
 *
 * Remplace l'ancienne page ParcoursEleve (qui contenait mini-QCM + missions).
 * Approche pure consultation magazine : on coche ce qu'on a lu, on retrouve
 * ce qu'on a sauvegardé, on voit la progression sans pression du score.
 *
 * État persisté en localStorage : { read: [slug], bookmarks: [slug] }
 *
 * URL canonique : /parcours (préservée pour SEO + liens externes)
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpenCheck, Bookmark, BookmarkCheck, ArrowRight, Sparkles,
  CheckCircle2, Circle, Clock, Newspaper, TrendingUp,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ETM_ISSUES_INDEX } from '../data/etmIssues';
import useSeo from '../hooks/useSeo';

const LS_KEY = 'c2r-journal-v1';

const loadJournal = () => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return { read: [], bookmarks: [] };
    const parsed = JSON.parse(raw);
    return {
      read: Array.isArray(parsed.read) ? parsed.read : [],
      bookmarks: Array.isArray(parsed.bookmarks) ? parsed.bookmarks : [],
    };
  } catch {
    return { read: [], bookmarks: [] };
  }
};

const saveJournal = (j) => {
  try { localStorage.setItem(LS_KEY, JSON.stringify(j)); } catch { /* ignore */ }
};

export default function MonJournal() {
  const [journal, setJournal] = useState(() => loadJournal());

  useEffect(() => { saveJournal(journal); }, [journal]);

  useSeo({
    title: 'Mes lectures · Code 2 Roues',
    description:
      "Suivez votre lecture des 9 dossiers magazine moto : marquez comme lu, ajoutez en favori, retrouvez vos articles. Une approche sans score, sans pression — la lecture moto à votre rythme.",
    path: '/parcours',
  });

  const published = useMemo(
    () => ETM_ISSUES_INDEX.filter((i) => i.status === 'published'),
    [],
  );

  const stats = useMemo(() => {
    const total = published.length;
    const read = published.filter((i) => journal.read.includes(i.slug)).length;
    const bookmarked = published.filter((i) => journal.bookmarks.includes(i.slug)).length;
    return { total, read, bookmarked, pct: total > 0 ? Math.round((read / total) * 100) : 0 };
  }, [published, journal]);

  const toggleRead = (slug) => {
    setJournal((prev) => {
      const isRead = prev.read.includes(slug);
      return {
        ...prev,
        read: isRead ? prev.read.filter((s) => s !== slug) : [...prev.read, slug],
      };
    });
  };

  const toggleBookmark = (slug) => {
    setJournal((prev) => {
      const isMarked = prev.bookmarks.includes(slug);
      return {
        ...prev,
        bookmarks: isMarked
          ? prev.bookmarks.filter((s) => s !== slug)
          : [...prev.bookmarks, slug],
      };
    });
  };

  const bookmarkedIssues = published.filter((i) => journal.bookmarks.includes(i.slug));

  return (
    <div className="min-h-screen" data-testid="mon-journal-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* HERO */}
        <div className="mb-10">
          <div className="inline-flex items-center gap-2 mb-3 text-[10px] font-black uppercase tracking-[0.22em] text-[hsl(var(--primary))]">
            <Sparkles className="w-3.5 h-3.5" />
            <span>Mon espace lecteur</span>
          </div>
          <h1
            className="text-3xl sm:text-4xl font-black tracking-tight leading-[1.05] mb-3"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Mes lectures
          </h1>
          <p className="text-base text-foreground/80 leading-relaxed max-w-2xl">
            Cochez ce que vous avez lu, mettez en favoris ce que vous voulez relire. Pas de score, pas de chrono — juste votre rythme.
          </p>
        </div>

        {/* STATS BAR */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-10">
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-500/10 text-emerald-600 flex items-center justify-center">
                <BookOpenCheck className="w-5 h-5" />
              </div>
              <div>
                <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Dossiers lus</div>
                <div className="text-xl font-black">{stats.read}<span className="text-sm font-normal text-muted-foreground">/{stats.total}</span></div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-500/10 text-amber-600 flex items-center justify-center">
                <BookmarkCheck className="w-5 h-5" />
              </div>
              <div>
                <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Favoris</div>
                <div className="text-xl font-black">{stats.bookmarked}</div>
              </div>
            </CardContent>
          </Card>
          <Card className="sm:col-span-1 col-span-2">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] flex items-center justify-center">
                <TrendingUp className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Progression</div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                    <div
                      className="h-full bg-[hsl(var(--primary))] rounded-full transition-all"
                      style={{ width: `${stats.pct}%` }}
                    />
                  </div>
                  <div className="text-sm font-black whitespace-nowrap">{stats.pct}%</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* FAVORIS RAPIDES */}
        {bookmarkedIssues.length > 0 && (
          <section className="mb-10">
            <h2 className="text-xs font-black uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-2">
              <Bookmark className="w-3.5 h-3.5 text-amber-500" /> Mes favoris
            </h2>
            <div className="flex flex-wrap gap-2">
              {bookmarkedIssues.map((it) => (
                <Link
                  key={it.slug}
                  to={`/apprendre/${it.slug}`}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-amber-500/40 bg-amber-500/5 hover:bg-amber-500/10 text-xs font-bold transition-colors"
                  data-testid={`journal-bookmark-${it.slug}`}
                >
                  {it.shortTitle}
                  <ArrowRight className="w-3 h-3" />
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* LISTE DES DOSSIERS */}
        <section>
          <h2
            className="text-2xl font-black mb-4"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Les 9 dossiers magazine
          </h2>
          <div className="space-y-2">
            {published.map((it) => {
              const isRead = journal.read.includes(it.slug);
              const isBookmarked = journal.bookmarks.includes(it.slug);
              return (
                <Card
                  key={it.slug}
                  className={isRead ? 'border-emerald-500/30 bg-emerald-500/5' : ''}
                  data-testid={`journal-issue-${it.slug}`}
                >
                  <CardContent className="p-4 flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => toggleRead(it.slug)}
                      className="flex-shrink-0"
                      title={isRead ? 'Marquer comme non lu' : 'Marquer comme lu'}
                      data-testid={`journal-toggle-read-${it.slug}`}
                    >
                      {isRead ? (
                        <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                      ) : (
                        <Circle className="w-6 h-6 text-muted-foreground hover:text-foreground transition-colors" />
                      )}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">
                        N°{String(it.number).padStart(2, '0')} · Thème {it.themeCode}
                      </div>
                      <Link
                        to={`/apprendre/${it.slug}`}
                        className="block font-black text-base leading-tight hover:text-[hsl(var(--primary))] transition-colors"
                        style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                      >
                        {it.title}
                      </Link>
                      <div className="flex items-center gap-2 mt-1 text-[11px] text-muted-foreground">
                        {it.issue?.readTimeMin && (
                          <span className="inline-flex items-center gap-1">
                            <Clock className="w-3 h-3" /> {it.issue.readTimeMin} min
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => toggleBookmark(it.slug)}
                        className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors ${
                          isBookmarked
                            ? 'bg-amber-500/15 text-amber-600 hover:bg-amber-500/25'
                            : 'hover:bg-muted text-muted-foreground hover:text-foreground'
                        }`}
                        title={isBookmarked ? 'Retirer des favoris' : 'Ajouter en favori'}
                        data-testid={`journal-toggle-bookmark-${it.slug}`}
                      >
                        {isBookmarked ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
                      </button>
                      <Button asChild size="sm" variant={isRead ? 'outline' : 'default'} className="ml-1">
                        <Link to={`/apprendre/${it.slug}`} data-testid={`journal-read-${it.slug}`}>
                          {isRead ? 'Relire' : 'Lire'}
                          <ArrowRight className="w-3.5 h-3.5 ml-1" />
                        </Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>

        {/* CTA enquêtes */}
        <div className="mt-10 p-6 rounded-2xl bg-muted/30 border border-border text-center">
          <Newspaper className="w-8 h-8 text-[hsl(var(--primary))] mx-auto mb-2" />
          <h3 className="text-lg font-black mb-1">Envie de varier ?</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Découvrez aussi nos enquêtes de terrain : témoignages réels, accidents analysés, données ONISR.
          </p>
          <Button asChild variant="outline">
            <Link to="/enquetes">
              Voir les enquêtes <ArrowRight className="w-4 h-4 ml-1.5" />
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
